/**
 * MergeRecordsModal — name the combined record, choose whether to remove the
 * originals, and confirm. Combines the selected records (2+) into one PDF: PDFs
 * contribute all their pages, uploaded photos each become a page. Removing the
 * originals is the default (their pages now live in the combined file), but the
 * recruiter can keep them.
 */
import { useState, useEffect, useCallback } from 'react';
import { X, Layers, Loader2 } from 'lucide-react';
import { Z } from '../ui';

interface Props {
  count: number;
  sizeLabel: string;
  defaultName: string;
  isMerging: boolean;
  onConfirm: (name: string, deleteOriginals: boolean) => void;
  onCancel: () => void;
}

export default function MergeRecordsModal({ count, sizeLabel, defaultName, isMerging, onConfirm, onCancel }: Props) {
  const [name, setName] = useState(defaultName);
  const [deleteOriginals, setDeleteOriginals] = useState(true);

  const onKey = useCallback(
    (e: KeyboardEvent) => { if (e.key === 'Escape' && !isMerging) onCancel(); },
    [onCancel, isMerging],
  );
  useEffect(() => {
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onKey]);

  const submit = () => { if (!isMerging) onConfirm(name.trim() || defaultName, deleteOriginals); };

  return (
    <div className={`fixed inset-0 ${Z.modal} overflow-y-auto`}>
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={isMerging ? undefined : onCancel}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
          role="dialog"
          aria-modal="true"
          aria-labelledby="merge-records-title"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-900/30">
                <Layers size={20} className="text-primary-600 dark:text-primary-400" />
              </div>
              <h2 id="merge-records-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Combine {count} records
              </h2>
            </div>
            <button
              onClick={onCancel}
              disabled={isMerging}
              className="rounded p-1 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-4 px-6 py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The {count} selected records ({sizeLabel}) will be combined into one PDF, in the
              order shown. The AI reviews the combined file overnight like any upload.
            </p>
            <div>
              <label htmlFor="merge-name-input" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name the combined record
              </label>
              <input
                id="merge-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                maxLength={160}
                disabled={isMerging}
                autoFocus
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>
            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={deleteOriginals}
                onChange={(e) => setDeleteOriginals(e.target.checked)}
                disabled={isMerging}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
              />
              <span>
                Remove the original records after combining
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {deleteOriginals
                    ? 'The originals are permanently deleted once combined.'
                    : 'The originals are kept alongside the combined record.'}
                </span>
              </span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/40">
            <button
              onClick={onCancel}
              disabled={isMerging}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={isMerging}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isMerging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
              {isMerging ? 'Combining…' : 'Combine records'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
